import { lazy } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router'
import { useWs } from './workspace'
import { AppLayout } from './layouts/app-layout'
import { AuthLayout } from './layouts/auth-layout'
import type { Permission } from '@/domain/vacations'

const LoginPage = lazy(() => import('@/features/auth/login-page'))
const SignupPage = lazy(() => import('@/features/auth/signup-page'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/forgot-password-page'))
const OrganizationsPage = lazy(() => import('@/features/onboarding/organizations-page'))
const OnboardingPage = lazy(() => import('@/features/onboarding/onboarding-page'))
const OverviewPage = lazy(() => import('@/features/overview/overview-page'))
const NewRequestPage = lazy(() => import('@/features/requests/new-request-page'))
const MyRequestsPage = lazy(() => import('@/features/requests/my-requests-page'))
const CalendarPage = lazy(() => import('@/features/calendar/calendar-page'))
const ApprovalsPage = lazy(() => import('@/features/approvals/approvals-page'))
const TeamPage = lazy(() => import('@/features/team/team-page'))
const ReportsPage = lazy(() => import('@/features/reports/reports-page'))
const PeoplePage = lazy(() => import('@/features/people/people-page'))
const SettingsPage = lazy(() => import('@/features/settings/settings-page'))

/** Hidden navigation is only convenience: the API/RLS enforce permissions again. */
function RequirePermission({ permission }: { permission: Permission }) {
  const ws = useWs()
  return ws.can(permission) ? <Outlet /> : <Navigate to="/" replace />
}

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  { path: '/organizations', element: <OrganizationsPage /> },
  { path: '/onboarding', element: <OnboardingPage /> },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: '/new', element: <NewRequestPage /> },
      { path: '/requests', element: <MyRequestsPage /> },
      { path: '/calendar', element: <CalendarPage /> },
      { element: <RequirePermission permission="requests.approve" />, children: [{ path: '/approvals', element: <ApprovalsPage /> }] },
      {
        element: <RequirePermission permission="team.view" />,
        children: [
          { path: '/team', element: <TeamPage /> },
          { path: '/reports', element: <ReportsPage /> },
        ],
      },
      { element: <RequirePermission permission="people.manage" />, children: [{ path: '/people', element: <PeoplePage /> }] },
      { element: <RequirePermission permission="settings.manage" />, children: [{ path: '/settings', element: <SettingsPage /> }] },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])
